import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from '../api.service';
import { map, Observable } from 'rxjs';
import {
  GetAnalysesForSampleData,
  GetAnalysesForSampleRaw,
  SampleData,
  SampleDataRaw,
} from '../../interfaces/sample-test-panel';

@Injectable({
  providedIn: 'root',
})
export class SampletestService {
  constructor(private apiService: ApiService) {}

  getAllSamples(): Observable<any> {
    return this.apiService.get(`/v1/samples`);
  }

  getAllAnalysisForSample(sampleId: string): Observable<any> {
    return this.apiService.get(`/v1/samples/${sampleId}/analyses`);
  }

  getAnalysesForSample(sampleId: string): Observable<GetAnalysesForSampleData[]> {
    return this.apiService.get<ApiResponse<GetAnalysesForSampleRaw[]>>(`/v1/samples/${sampleId}/analyses`).pipe(
      map(({ data }) => {
        return data.map((a) => {
          return {
            analysisId: String(a.AnalysisID),
            analysisName: a.AnalysisName,
            analysisDescription: a.AnalysisDescription,
            status: a.Status,
            panelId: a.PanelID,
            folderName: a.FolderName,
            createdByUser: a.CreatedByUser,
            orgId: a.OrgID,
            createdOn: a.CreatedOn,
            completedOn: a.CompletedOn,
            isArchived: a.IsArchived,
            percentComplete: a.PercentComplete,
            sampleId: a.SampleID,
            sampleName: a.SampleName,
            sampleDescription: a.SampleDescription,
            panelName: a.PanelName,
            panelDescription: a.PanelDescription,
            userName: a.UserName,
            groups: (a.Groups ?? []).map((group) => {
              return {
                groupId: String(group.GroupID),
                groupName: group.GroupName,
                isPrimary: group.IsPrimary,
              };
            }),
            inputs: a.Inputs,
            // (a.Inputs ?? a.AnalysisID === 1060)
            //   ? 'combine1p11'
            //   : a.AnalysisID === 1059
            //     ? 'remap1p11'
            //     : a.AnalysisID === 1061
            //       ? 'SV-Discovery'
            //       : 'dummy-data',
            application: a.Application,
          };
        });
      })
    );
  }

  runSample(
    analysisName: any,
    analysisDescription: any,
    panelId: number,
    sampleId: number,
    versionId: number
  ): Observable<any> {
    const runData: any = {
      analysis_name: analysisName,
      panel_id: panelId,
      sample_id: sampleId,
      version_id: versionId,
    };
    if (analysisDescription) {
      runData['analysis_description'] = analysisDescription;
    }
    return this.apiService.post(`/v1/jobs/analysis-run`, runData);
  }

  getSampleInfo(sampleId: number): Observable<SampleData> {
    return this.apiService.get<ApiResponse<SampleDataRaw>>(`/v1/samples/${sampleId}`).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(`Invalid response for sampleId ${sampleId}`);
        }

        const { data } = response;

        return {
          sampleId: data.SampleID,
          sampleName: data.SampleName,
          description: data.Description,
          instrumentRunNames: data.InstrumentRunNames,
          analysisNames: data.AnalysisNames,
          userName: data.UserName,
          /** ISO 8601 UTC, e.g. "2026-07-31T12:34:56Z". */
          createdOn: data.CreatedOn,
          groups: (data.Groups ?? []).map((group) => {
            return {
              groupId: group.GroupID,
              groupName: group.GroupName,
              isPrimary: group.IsPrimary,
            };
          }),
        };
      })
    );
  }

  deleteSample(sampleId: number, analysisID?: number): Observable<any> {
    if (analysisID) {
      return this.apiService.delete(`/v1/samples/${sampleId}?analysis_id=${analysisID}`);
    }
    return this.apiService.delete(`/v1/samples/${sampleId}`);
  }

  editSample(sampleId: number, sampleData: any): Observable<any> {
    return this.apiService.patch(`/v1/samples/${sampleId}`, sampleData);
  }

  getSamplesForHomePage(): Observable<any> {
    return this.getAllSamples().pipe(
      map((response) => {
        if (response && response.data) {
          const sortedSamples = response.data.sort((a: any, b: any) => {
            const dateA = new Date(a.CreatedOn).getTime();
            const dateB = new Date(b.CreatedOn).getTime();
            return dateB - dateA;
          });
          return sortedSamples.slice(0, 5);
        }
        return [];
      })
    );
  }

  getAllApplicationsForOrg(): Observable<any> {
    return this.apiService.get<any>(`/v1/versions`);
  }
}
